import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import AMCReportTable from "./AMCReportTable";
import { useAppSelector } from "../../../redux/hooks";
import { formatAdType } from "../utils/utils";
import { useAcosOrRoas } from "../../../utils/acosOrRoas";
import PieChart from "../../../components/core/blocks/PieChart";
import { ButtonGroup } from "../../../components/core/basic/ButtonGroup";
import { formatMetricValue } from "../../../utils/formatNumber";
import Card from "../../../components/core/basic/Card";
import { Box, Grid, VStack, Wrap } from "../../../../styled-system/jsx";
import { css } from "../../../../styled-system/css";

const TOTAL_METRICS = ["total_product_sales", "total_cost", "impressions", "clicks", "conversions"];

const CHART_METRIC_OPTIONS = [
  {
    value: "total_product_sales",
    label: ($) => $.amc["Total Product Sales"],
    formatOptions: { format: "currency" },
  },
  {
    value: "total_cost",
    label: ($) => $.amc["Total Cost"],
    formatOptions: { format: "currency" },
  },
  {
    value: "campaign_cnt",
    label: ($) => $.amc["Campaign Count"],
    formatOptions: { format: "number", decimalPlaces: 0 },
  },
  {
    value: "clicks",
    label: ($) => $.amc["Clicks"],
    formatOptions: { format: "number", decimalPlaces: 0 },
  },
  {
    value: "impressions",
    label: ($) => $.amc["Impressions"],
    formatOptions: { format: "number", decimalPlaces: 0 },
  },
];

const Metric = ({ title, value, formatOptions }) => {
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const formattedValue = formatMetricValue(value, { ...formatOptions, marketPlace });
  return (
    <Box fontSize={"medium"} textAlign="center">
      <span className={css({ whiteSpace: "nowrap" })}>{title}</span>
      <span className={css({ fontSize: "large", fontWeight: "bold", display: "block" })}>{formattedValue}</span>
    </Box>
  );
};

const AMCFirstTouchAttrReport = ({ reportData, reportDetails, queryDetails }) => {
  const { t } = useTranslation(["amc"]);
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const acosOrRoas = useAcosOrRoas();
  const [pieChartMetric, setPieChartMetric] = useState(CHART_METRIC_OPTIONS[0].value);
  const selectedMetric = useMemo(
    () => CHART_METRIC_OPTIONS.find((option) => option.value === pieChartMetric),
    [pieChartMetric],
  );

  const buttonGroupOptions = useMemo(
    () => CHART_METRIC_OPTIONS.map((option) => ({ value: option.value, label: t(option.label) })),
    [t],
  );

  const byAdType = useMemo(() => {
    const totalsByAdType = reportData.reduce((acc, row) => {
      const adType = row["ad_product_type"];

      if (!acc[adType]) {
        acc[adType] = {
          campaign_cnt: 0,
          ...TOTAL_METRICS.reduce((acc, metric) => {
            acc[metric] = 0;
            return acc;
          }, {}),
        };
      }
      acc[adType]["campaign_cnt"] += 1;
      TOTAL_METRICS.forEach((metric) => {
        acc[adType][metric] += row[metric];
      });
      return acc;
    }, {});
    Object.keys(totalsByAdType).forEach((adType) => {
      const total = totalsByAdType[adType];
      total["roas"] = total["total_product_sales"] / total["total_cost"];
      total["acos"] = total["total_cost"] / total["total_product_sales"];
      total["ctr"] = total["clicks"] / total["impressions"];
      total["conv_rate"] = total["conversions"] / total["clicks"];
    });
    return totalsByAdType;
  }, [reportData]);

  const pieChartData = useMemo(() => {
    return (
      Object.keys(byAdType)
        .map((adType) => {
          const total = byAdType[adType];
          return {
            name: formatAdType(adType),
            y: total[pieChartMetric],
          };
        })
        ?.filter((type) => type.y) ?? []
    );
  }, [byAdType, pieChartMetric]);

  return (
    <>
      <Grid columns={{ base: 1, sm: 2 }}>
        <VStack gap={"small"}>
          <PieChart
            data={pieChartData}
            nameAccessor={(d) => d.name}
            accessor={(d) => d.y}
            formatter={(v) => formatMetricValue(v, { ...selectedMetric.formatOptions, marketPlace })}
            height={460}
          />
          <ButtonGroup
            buttons={buttonGroupOptions}
            value={selectedMetric.value}
            onSelect={(value) => setPieChartMetric(value)}
            colorPalette={"buttonGroup.fuchsia"}
          />
        </VStack>
        <VStack gap="small">
          {Object.keys(byAdType).map((adType) => {
            const total = byAdType[adType];
            return (
              <Card key={adType} variant="flat" padding={"medium"}>
                <h4
                  className={css({
                    margin: 0,
                    fontWeight: "bold",
                    fontSize: "medium",
                    marginLeft: "medium",
                  })}
                >
                  {formatAdType(adType)}
                </h4>
                <Wrap columnGap="large" rowGap={"small"} justifyContent="space-around">
                  <Metric
                    title={t(($) => $.amc["Campaign Count"])}
                    value={total["campaign_cnt"]}
                    formatOptions={{ format: "number", decimalPlaces: 0 }}
                  />
                  <Metric
                    title={t(($) => $.amc["Total Product Sales"])}
                    value={total["total_product_sales"]}
                    formatOptions={{ format: "currency", compact: true }}
                  />
                  <Metric
                    title={t(($) => $.amc["Total Cost"])}
                    value={total["total_cost"]}
                    formatOptions={{ format: "currency", compact: true }}
                  />
                  <Metric
                    title={t(($) => $.amc["Impressions"])}
                    value={total["impressions"]}
                    formatOptions={{ format: "number", decimalPlaces: 0 }}
                  />
                  <Metric
                    title={t(($) => $.amc["Clicks"])}
                    value={total["clicks"]}
                    formatOptions={{ format: "number", decimalPlaces: 0 }}
                  />
                  <Metric
                    title={t(($) => $.amc["Conversions"])}
                    value={total["conversions"]}
                    formatOptions={{ format: "number", decimalPlaces: 0 }}
                  />
                  {acosOrRoas(
                    <Metric
                      title={t(($) => $.amc["ACOS"])}
                      value={total["acos"]}
                      formatOptions={{ format: "percent" }}
                    />,
                    <Metric
                      title={t(($) => $.amc["ROAS"])}
                      value={total["roas"]}
                      formatOptions={{ format: "number" }}
                    />,
                  )}
                  <Metric title={t(($) => $.amc["CTR"])} value={total["ctr"]} formatOptions={{ format: "percent" }} />
                  <Metric
                    title={t(($) => $.amc["Conversion Rate"])}
                    value={total["conv_rate"]}
                    formatOptions={{ format: "percent" }}
                  />
                </Wrap>
              </Card>
            );
          })}
        </VStack>
      </Grid>
      <AMCReportTable reportData={reportData} reportDetails={reportDetails} queryDetails={queryDetails} />
    </>
  );
};

export default AMCFirstTouchAttrReport;
